import React from 'react';
import {MultiSelectTreeViewProps, TreeView} from '@mui/x-tree-view';
import {IDiplomatObject} from 'types/DiplomatObject';
import {IDiplomatFolder} from 'types/DiplomatFolder';
import {FixedSizeList as List} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import TreeViewNode from 'components/TreeViewNode';
import {useBuildTreeExpandCollapseContext} from 'components/ContextMenu/hooks/useBuildTreeExpandCollapseContext';
import {treeExpandCollapseContext} from 'components/ContextMenu/treeExpandCollapseContext';
import FoldersBuilder from 'features/layout/redux/helpers/FoldersBuilder';
import {menuContext} from './ContextMenu/context';
import {useBuildTreeContextMenu} from './ContextMenu/hooks/useBuildTreeContextMenu';
import {ExpandCollapseContextMenu} from 'features/layout/SideNav/components/ExpandCollapseContextMenu';

interface BaseRecursiveTreeViewProps {
   objects: IDiplomatObject[];
   folders: IDiplomatFolder[];
   className?: string;
}

interface MultiRecursiveTreeViewProps extends BaseRecursiveTreeViewProps {
   onNodeSelect: MultiSelectTreeViewProps['onNodeSelect'];
   multiSelect?: MultiSelectTreeViewProps['multiSelect'];
}

type RecursiveTreeViewProps = MultiRecursiveTreeViewProps;

const RecursiveTreeView = ({
   objects,
   folders,
   className,
   onNodeSelect,
   multiSelect = true,
}: RecursiveTreeViewProps) => {
   const foldersBuilder = React.useMemo(() => new FoldersBuilder(folders), [folders]);

   const nodes = React.useMemo(() => foldersBuilder.buildFilteredFoldersWithObjects(objects), [
      objects,
   ]);

   const {Provider: TreeExpandCollapseProvider} = treeExpandCollapseContext;
   const {
      contextValue: expandCollapseContextValue,
      flattened,
      infiniteItemHeight,
      infiniteListClassName,
   } = useBuildTreeExpandCollapseContext(nodes);

   const loadMoreItems = React.useCallback(() => null, []);
   const isItemLoaded = React.useCallback((i) => !!flattened, [flattened]);

   const [listHeight, setListHeight] = React.useState(370);

   const {Provider} = menuContext;
   const {contextValue} = useBuildTreeContextMenu();

   const [anchorIndex, setAnchorIndex] = React.useState<number | null>(null);
   const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

   const handleNodeClick = React.useCallback(
      (event: React.MouseEvent, currentIndex: number) => {
         if (!multiSelect) return;

         const clickedId = flattened[currentIndex].id;

         if (event.shiftKey && anchorIndex !== null) {
            // SHIFT + CLICK = range selection
            const start = Math.min(anchorIndex, currentIndex);
            const end = Math.max(anchorIndex, currentIndex);
            const range = flattened.slice(start, end + 1).map((node) => node.id);
            const newSelected = Array.from(new Set([...selectedIds, ...range]));

            setSelectedIds(newSelected);
            onNodeSelect?.(event as any, newSelected);
         } else if (event.ctrlKey || event.metaKey) {
            // CTRL or CMD + CLICK = toggle
            const isSelected = selectedIds.includes(clickedId);
            const newSelected = isSelected
               ? selectedIds.filter((id) => id !== clickedId)
               : [...selectedIds, clickedId];

            setAnchorIndex(currentIndex); // optional: update anchor
            setSelectedIds(newSelected);
            onNodeSelect?.(event as any, newSelected);
         } else {
            // Plain CLICK = single selection
            setAnchorIndex(currentIndex);
            setSelectedIds([clickedId]);
            onNodeSelect?.(event as any, [clickedId]);
         }
      },
      [anchorIndex, flattened, multiSelect, onNodeSelect, selectedIds]
   );

   return (
      <TreeExpandCollapseProvider value={expandCollapseContextValue}>
         <TreeView className={className} selected={selectedIds} multiSelect={multiSelect}>
            <Provider value={contextValue}>
               <InfiniteLoader
                  isItemLoaded={isItemLoaded}
                  itemCount={flattened.length}
                  loadMoreItems={loadMoreItems}
               >
                  {({onItemsRendered, ref}) => (
                     <List
                        className={infiniteListClassName}
                        height={listHeight}
                        itemCount={flattened.length}
                        itemSize={infiniteItemHeight}
                        onItemsRendered={onItemsRendered}
                        ref={ref}
                        width="100%"
                     >
                        {({index, style}) => (
                           <div
                              key={flattened[index].id}
                              style={{...style}}
                              onClick={(e) => handleNodeClick(e, index)}
                           >
                              <TreeViewNode
                                 style={{
                                    marginLeft: `${flattened[index]._state.depth * 1}rem`,
                                 }}
                                 data={flattened[index]}
                                 selected={selectedIds.includes(flattened[index].id)}
                              />
                           </div>
                        )}
                     </List>
                  )}
               </InfiniteLoader>
               <ExpandCollapseContextMenu />
            </Provider>
         </TreeView>
      </TreeExpandCollapseProvider>
   );
};

export default RecursiveTreeView;
